import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO, injectJSONLD } from '@/hooks/useSEO';
import { articles, navCategories } from '@/mocks/navigationData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    if (!article) {
      navigate('/', { replace: true });
    }
  }, [article, navigate]);

  const defaultArticle = articles[0];
  const effectiveArticle = article || defaultArticle;
  const category = effectiveArticle
    ? navCategories.find((c) => c.slug === effectiveArticle.category)
    : null;
  const relatedArticles = effectiveArticle
    ? articles.filter((a) => a.slug !== effectiveArticle.slug && a.category === effectiveArticle.category).slice(0, 2)
    : [];

  useSEO({
    title: `${effectiveArticle.title} - 蓝导航`,
    description: effectiveArticle.excerpt,
    keywords: `${effectiveArticle.tags.join(',')},蓝导航,网址导航`,
    canonicalUrl: `https://www.lan-dao.com/article/${effectiveArticle.slug}`,
  });

  useEffect(() => {
    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: effectiveArticle.title,
      description: effectiveArticle.excerpt,
      author: {
        '@type': 'Person',
        name: effectiveArticle.author,
      },
      datePublished: effectiveArticle.date,
      publisher: {
        '@type': 'Organization',
        name: '蓝导航',
        url: 'https://www.lan-dao.com/',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.lan-dao.com/article/${effectiveArticle.slug}`,
      },
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.lan-dao.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '文章',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: effectiveArticle.title,
        },
      ],
    });
  }, [effectiveArticle]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p className="text-foreground-500">跳转中...</p>
        </div>
        <Footer />
      </div>
    );
  }

  const contentParagraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <Breadcrumb />

      <main>
        <article className="max-w-[900px] mx-auto px-4 md:px-6 py-6 md:py-10">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors"
                >
                  <i className={`${category.icon} text-xs`}></i>
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-xs text-accent-600"></i>
                </div>
                {article.author}
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm"></i>
                {article.date}
              </div>
            </div>
          </header>

          <div className="mb-8 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-[300px] md:h-[400px] object-cover object-top"
            />
          </div>

          <div className="prose-custom text-foreground-800 leading-relaxed">
            {contentParagraphs.map((paragraph, i) => {
              if (paragraph.startsWith('**') && paragraph.match(/^\*\*.*?\*\*: /)) {
                const [title, ...rest] = paragraph.split(': ');
                const cleanTitle = title.replace(/\*\*/g, '');
                const content = rest.join(': ');
                return (
                  <div key={i} className="mb-5">
                    <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                      {cleanTitle}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-700">{content}</p>
                  </div>
                );
              }
              if (paragraph.trim().match(/^\d+\.\s/)) {
                const items = paragraph.split('\n').filter(Boolean);
                const listItems = items.length > 1 ? items : paragraph.split(/(?=\d+\.\s)/).filter(Boolean);
                return (
                  <div key={i} className="mb-5">
                    {listItems.length > 1 ? (
                      <ol className="space-y-3 list-decimal list-inside">
                        {listItems.map((item, j) => {
                          const cleaned = item.replace(/^\d+\.\s/, '');
                          if (cleaned.includes('**')) {
                            const parts = cleaned.split('**');
                            return (
                              <li key={j} className="text-sm md:text-base text-foreground-700 pl-1">
                                <strong className="font-semibold text-foreground-900">
                                  {parts[1]}
                                </strong>
                                {parts[2] || ''}
                              </li>
                            );
                          }
                          return (
                            <li key={j} className="text-sm md:text-base text-foreground-700 pl-1">
                              {cleaned}
                            </li>
                          );
                        })}
                      </ol>
                    ) : (
                      <p className="text-sm md:text-base text-foreground-700 mb-2">{firstLine}</p>
                    )}
                  </div>
                );
              }
              return (
                <p key={i} className="text-sm md:text-base text-foreground-700 mb-5">
                  {paragraph}
                </p>
              );
            })}
          </div>

          <footer className="mt-10 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to="/"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                返回首页
              </Link>
            </div>
          </footer>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-[900px] mx-auto px-4 md:px-6 py-10 border-t border-background-200">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">
              相关文章
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group flex gap-3 p-3 bg-background-50 rounded-xl border border-background-200 hover:border-accent-300 transition-all duration-200"
                >
                  <div className="w-20 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}